import React, { useState } from "react";

// Grouped so "this" and "last" sit side-by-side for each period —
// easier to scan than one long flat list.
const PRESET_ROWS = [
  { period: "Week", this: "this_week", last: "last_week" },
  { period: "Month", this: "this_month", last: "last_month" },
  { period: "Quarter", this: "this_quarter", last: "last_quarter" },
  { period: "Year", this: "this_year", last: "last_year" },
];

// today's date as YYYY-MM-DD, for the <input type="date"> max attribute
const todayStr = () => new Date().toISOString().split("T")[0];

export default function ExportModal({
  open,
  onClose,
  onExport, // (format: "excel" | "pdf", range: { preset } | { from, to } | {}) => void
  exporting,
  title = "Export Timesheets",
}) {
  const [selectedPreset, setSelectedPreset] = useState("this_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [error, setError] = useState("");

  if (!open) return null;

  const buildRange = () => {
    if (selectedPreset === "all") return {};
    if (selectedPreset !== "custom") return { preset: selectedPreset };

    if (!customFrom || !customTo) {
      setError("Please select both a start and end date");
      return null;
    }
    if (customFrom > customTo) {
      setError("Start date must be before end date");
      return null;
    }
    setError("");
    return { from: customFrom, to: customTo };
  };

  const handleExport = (format) => {
    const range = buildRange();
    if (range === null) return; // validation failed, error already set
    onExport(format, range);
  };

  const presetBtnClass = (value) =>
    `px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
      selectedPreset === value
        ? "bg-orange-500/20 border-orange-500 text-orange-400"
        : "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-650 hover:border-gray-500"
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h2 className="text-white font-semibold text-lg">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wide mb-2 block">
              Date Range
            </label>

            {/* This / Last header row */}
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 mb-1 px-1">
              <span></span>
              <span className="text-[11px] text-gray-500 uppercase tracking-wide text-center">
                This
              </span>
              <span className="text-[11px] text-gray-500 uppercase tracking-wide text-center">
                Last
              </span>
            </div>

            <div className="space-y-2">
              {PRESET_ROWS.map((row) => (
                <div
                  key={row.period}
                  className="grid grid-cols-[1fr_1fr_1fr] gap-2 items-center"
                >
                  <span className="text-sm text-gray-400">{row.period}</span>
                  <button
                    onClick={() => {
                      setSelectedPreset(row.this);
                      setError("");
                    }}
                    className={presetBtnClass(row.this)}
                  >
                    This {row.period}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedPreset(row.last);
                      setError("");
                    }}
                    className={presetBtnClass(row.last)}
                  >
                    Last {row.period}
                  </button>
                </div>
              ))}
            </div>

            {/* Custom / All Time */}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                onClick={() => {
                  setSelectedPreset("custom");
                  setError("");
                }}
                className={presetBtnClass("custom")}
              >
                Custom Range
              </button>
              <button
                onClick={() => {
                  setSelectedPreset("all");
                  setError("");
                }}
                className={presetBtnClass("all")}
              >
                All Time
              </button>
            </div>
          </div>

          {selectedPreset === "custom" && (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-wide">
                  From
                </label>
                <input
                  type="date"
                  value={customFrom}
                  max={customTo || todayStr()}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 uppercase tracking-wide">
                  To
                </label>
                <input
                  type="date"
                  value={customTo}
                  min={customFrom}
                  max={todayStr()}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-700">
          <button
            onClick={() => handleExport("excel")}
            disabled={exporting}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg shadow-md transition-all ${
              exporting
                ? "bg-emerald-700 cursor-not-allowed opacity-60"
                : "bg-emerald-600 hover:bg-emerald-500 active:scale-95"
            }`}
          >
            {exporting ? "Exporting..." : "Excel"}
          </button>
          <button
            onClick={() => handleExport("pdf")}
            disabled={exporting}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg shadow-md transition-all ${
              exporting
                ? "bg-red-700 cursor-not-allowed opacity-60"
                : "bg-red-600 hover:bg-red-500 active:scale-95"
            }`}
          >
            {exporting ? "Exporting..." : "PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
